import { Prognose, PrognoseStatusOptions, usePutPrognoseById } from "@/entities/dossier-details";
import {
    Button,
    Dialog,
    DialogBody,
    DialogContainer,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    ErrorDisplay,
    Form,
    FormFieldInput,
    SelectFormField,
} from "@/shared/components";
import { FormFieldInputDateTime } from "@/shared/components/ui/input-datetime-formfield.tsx";
import { zodResolver } from "@hookform/resolvers/zod";
import { isEqual } from "date-fns";
import { useForm } from "react-hook-form";
import { z } from "zod";

const statusOptions = Object.values(PrognoseStatusOptions);
const prognoseSchema = z.object({
    datumTijd: z.date({ error: "Vul een tijd en datum in voor de prognose" }),
    status: z.string(),
    toelichting: z.string().optional(),
});

type PrognoseData = z.infer<typeof prognoseSchema>;

type NewEditPrognoseDialogProps = {
    dossierId: string;
    open: boolean;
    setOpen: (open: boolean) => void;
    defaultValues?: Partial<PrognoseData>;
};

export function NewEditPrognoseDialog({ dossierId, open, setOpen, defaultValues }: NewEditPrognoseDialogProps) {
    const hasExistingPrognose = defaultValues && Object.keys(defaultValues).length > 0;
    const putPrognose = usePutPrognoseById();
    const form = useForm({
        resolver: zodResolver(prognoseSchema),
        defaultValues: {
            status: Prognose.statusDefaultValue(defaultValues?.status),
            toelichting: defaultValues?.toelichting ?? undefined,
            datumTijd: defaultValues?.datumTijd ?? undefined,
        },
    });

    const onSubmit = (data: PrognoseData) => {
        if (defaultValues && !hasChanges(defaultValues, data)) {
            setOpen(false);
            return;
        }

        const { datumTijd, status, toelichting } = data;

        putPrognose.mutate(
            { dossierId, datumTijd: datumTijd.toISOString(), status, toelichting, gewijzigdDatumTijd: new Date().toISOString() },
            { onSuccess: () => setOpen(false) }
        );
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContainer>
                <DialogHeader>
                    <DialogTitle>Prognose {hasExistingPrognose ? "aanpassen" : "toevoegen"}</DialogTitle>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}>
                        <DialogBody className="grid gap-4">
                            <div className="flex flex-row items-end gap-4">
                                <FormFieldInputDateTime form={form} name="datumTijd" label="Prognose" defaultDate={new Date()} />
                            </div>
                            <div className="flex flex-col gap-1">
                                <div className="grid grid-cols-[0.3fr_0.7fr] items-center gap-x-4">
                                    <SelectFormField
                                        form={form}
                                        name="status"
                                        label="Status"
                                        options={statusOptions.map((v) => ({ value: v, label: v }))}
                                    />
                                    <FormFieldInput
                                        form={form}
                                        name="toelichting"
                                        label="Toelichting"
                                        type="text"
                                        placeholder="Voeg een toelichting toe"
                                        optional
                                    />
                                </div>
                                <span className="text-s text-content-subtle leading-m font-normal">
                                    Status en toelichtingen worden na {hasExistingPrognose ? "opslaan" : "toevoegen"} in Wissel getoond
                                </span>
                            </div>
                        </DialogBody>
                        <DialogFooter>
                            <Button type="button" onClick={() => setOpen(false)} variant="tertiary">
                                Annuleren
                            </Button>
                            <Button type="submit" variant="primary" disabled={putPrognose.isPending}>
                                {hasExistingPrognose ? "Opslaan" : "Toevoegen"}
                            </Button>
                            {putPrognose.error && <ErrorDisplay error={putPrognose.error} />}
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContainer>
        </Dialog>
    );
}

const hasChanges = (prev: Partial<PrognoseData>, next: PrognoseData) => {
    const statusNotEqual = prev.status !== next.status;
    const toelichtingNotEqual = prev.toelichting !== next.toelichting;
    const datumTijdNotEqual = prev.datumTijd !== undefined ? !isEqual(prev.datumTijd, next.datumTijd) : next.datumTijd !== undefined;
    return statusNotEqual || toelichtingNotEqual || datumTijdNotEqual;
};
